/**
 * UK / country-specific "application essentials" — NI/SSN, right-to-work, tax IDs.
 * Schema swaps based on selected country so the form mirrors what local applications ask for.
 *
 * Editing pattern: always-inline editable, debounced auto-save, single "lock it in"
 * button at the bottom that flushes any pending save and shows a confirmation toast.
 * Matches EqualOppsSection and PersonalDetailsSection exactly — no more lock/unlock toggle.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LockKey, Globe } from "@phosphor-icons/react";

const COUNTRY_SCHEMAS = {
  UK: {
    label: "United Kingdom",
    fields: [
      ["ni_number", "National Insurance number", "QQ123456C — only stored in your vault, never shared on share-links"],
      ["right_to_work_status", "Right to work", "e.g. British citizen, Settled status, Skilled Worker visa"],
      ["right_to_work_notes", "Visa / permit details", "expiry date, sponsor, restrictions (optional)"],
    ],
  },
  US: {
    label: "United States",
    fields: [
      ["ssn_last4", "SSN (last 4 digits)", "only ever store the last 4 in your vault"],
      ["right_to_work_status", "Work authorisation", "e.g. US citizen, Green Card, H1-B, OPT"],
      ["right_to_work_notes", "Visa details", "expiry, sponsor, transfer rules (optional)"],
    ],
  },
  EU: {
    label: "EU / EEA",
    fields: [
      ["tax_id", "Tax ID / fiscal code", "country-specific tax identifier"],
      ["right_to_work_status", "Work authorisation", "EU/EEA citizen, Blue Card, permit, etc."],
      ["right_to_work_notes", "Permit details", "expiry, restrictions (optional)"],
    ],
  },
  OTHER: {
    label: "Other",
    fields: [
      ["tax_id", "National ID / Tax number", "the equivalent ID applications in your country ask for"],
      ["right_to_work_status", "Work authorisation", "your right-to-work status / visa"],
      ["right_to_work_notes", "Permit details", "expiry, restrictions (optional)"],
    ],
  },
};

const COUNTRIES = Object.entries(COUNTRY_SCHEMAS).map(([k, v]) => [k, v.label]);

// Debounce (matches Work / Ed / Refs / Custom / PersonalDetails / EqualOpps)
function useDebounced(fn, delay = 700) {
  const ref = useRef(fn);
  const timer = useRef(null);
  useEffect(() => { ref.current = fn; }, [fn]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const debounced = (...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => ref.current(...args), delay);
  };
  debounced.cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  return debounced;
}

export default function UKEssentialsSection() {
  const [country, setCountry] = useState("UK");
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      const pi = data?.personal_info || {};
      setCountry(pi.country || "UK");
      setForm({
        ni_number: pi.ni_number || "",
        ssn_last4: pi.ssn_last4 || "",
        tax_id: pi.tax_id || "",
        right_to_work_status: pi.right_to_work_status || "",
        right_to_work_notes: pi.right_to_work_notes || "",
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const persist = useDebounced(async (nextForm, nextCountry) => {
    setSaving(true);
    try {
      const { data } = await api.get("/profile");
      const merged = { ...(data?.personal_info || {}), ...nextForm, country: nextCountry };
      await api.put("/profile", merged);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch {
      toast.error("Couldn't save changes");
    } finally { setSaving(false); }
  }, 700);

  const update = (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    setDirty(true);
    persist(next, country);
  };

  const updateCountry = (nextCountry) => {
    setCountry(nextCountry);
    setDirty(true);
    persist(form, nextCountry);
  };

  const lockIn = async () => {
    persist.cancel?.();
    setSaving(true);
    try {
      const { data } = await api.get("/profile");
      const merged = { ...(data?.personal_info || {}), ...form, country };
      await api.put("/profile", merged);
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
      toast.success("Locked into the vault");
    } catch {
      toast.error("Couldn't lock it in — try again");
    } finally { setSaving(false); }
  };

  const schema = COUNTRY_SCHEMAS[country] || COUNTRY_SCHEMAS.UK;

  return (
    <section className="card-hard rounded-md bg-white" data-testid="section-uk-essentials">
      <div className="flex items-center justify-between p-5 border-b-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-yellow)]">
        <div>
          <p className="label-tiny flex items-center gap-1.5"><LockKey size={12} weight="duotone"/> /application essentials</p>
          <h2 className="font-display text-2xl font-semibold tracking-tight">the stuff every form asks.</h2>
          <p className="text-xs text-[var(--brand-ink)]/60 mt-1">
            NI / SSN / Tax ID + right-to-work — entered once, never typed again. private to your vault, never on share links.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="font-mono-d text-xs text-[var(--brand-muted)]">saving…</span>}
          {justSaved && !saving && <span className="font-mono-d text-xs text-[color:var(--matcha-strong,#557755)]">saved ✓</span>}
          {dirty && !saving && !justSaved && <span className="font-mono-d text-xs text-[color:var(--brand-primary)]">unsaved</span>}
        </div>
      </div>

      <div className="p-5">
        <div className="mb-4">
          <label className="label-tiny mb-1.5 flex items-center gap-1.5"><Globe size={12} weight="duotone"/> country</label>
          <select data-testid="uk-country" value={country}
            onChange={(e) => updateCountry(e.target.value)}
            disabled={!loaded}
            className="card-hard rounded-md px-3 py-2 bg-white">
            {COUNTRIES.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-4">
          {schema.fields.map(([k, label, hint]) => (
            <div key={k}>
              <label className="label-tiny mb-1.5 block">{label}</label>
              <input
                data-testid={`uk-${k}`}
                value={form[k] || ""}
                onChange={(e) => update({ [k]: e.target.value })}
                placeholder={hint}
                disabled={!loaded}
                className="w-full card-hard rounded-md px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]"
              />
            </div>
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-dashed border-[var(--brand-ink)]/20 flex items-center gap-3 flex-wrap">
          <button onClick={lockIn} disabled={saving || !loaded} data-testid="uk-lock-in"
            className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-[var(--brand-ink)] disabled:opacity-60">
            <LockKey size={14} weight="bold"/> {saving ? "locking…" : "lock it in"}
          </button>
          <span className="text-xs text-[var(--brand-muted)]">
            auto-saves as you type · hit <b>lock it in</b> when you&apos;re done — it confirms your essentials are locked in for autofill.
          </span>
        </div>
      </div>
    </section>
  );
}
